/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { Hit } from '@algolia/client-search';

import { AudienceType } from '../algorithm/types';
import pearlApi from '../api/pearlApi';

let abortController: AbortController | null = null;

export async function fetchAlgoliaAutocompleteResults(
  query: string,
  onlyPublished: boolean,
  audience: AudienceType
): Promise<Array<Hit<AlgoliaQueryHitItem>>> {
  if (abortController) {
    abortController.abort(); // Abort the previous request
  }

  abortController = new AbortController(); // Create a new abort controller
  const signal = abortController.signal;

  try {
    const response = await pearlApi.post<{
      results: Array<AlgoliaSearchResponse>;
    }>(
      '/documentVersions/autocomplete-rt',
      {
        requests: [{ query }],
        onlyPublished: onlyPublished,
        persona: audience.persona,
        careCategory: audience.careCategory,
        patientGroup: audience.patientGroup,
      },
      {
        headers: {
          Accept: 'application/json',
        },
        signal,
      }
    );

    if (!!response?.data) {
      return response.data.results.flatMap((algoliaCollection) => algoliaCollection.hits);
    }
    return [];
  } finally {
    abortController = null;
  }
}

export function debouncePromise<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  time: number
): (...args: Parameters<T>) => ReturnType<T> {
  let timerId: NodeJS.Timeout | undefined;

  return function debounced(...args: Parameters<T>): ReturnType<T> {
    if (timerId) {
      clearTimeout(timerId);
    }

    return new Promise((resolve, reject) => {
      timerId = setTimeout(() => {
        fn(...args)
          .then(resolve)
          .catch(reject);
      }, time);
    }) as ReturnType<T>;
  };
}

type AlgoliaSearchResponse = {
  hits: Array<Hit<AlgoliaQueryHitItem>>;
};

// We have defined only the types which we are using in the autocomplete.
export type AlgoliaQueryHitItem = {
  title: string;
  flags: string[];
  queryID: string;
  doc_slug: string;
};
